import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { PROJECT_ID, ROUTE_PROJECT } from '../../constants';
import { useNavigate } from 'react-router-dom';
import IconButtonBox from '../../components/IconButtonBox';
import useProjectStore from '../../stores/ProjectStore';
import DialogBox from '../../components/DialogBox';
import OutlinedButton from '../../components/OutlinedButton';
import ProjectApi from '../../api/ProjectApi';
import TextInputBox from '../../components/TextInputBox';
import NewTableBox from '../../components/NewTableBox';
import { getLocalStorage, useError } from '../../utils/functions';
import usePaginationStore from '../../stores/PaginationStore';
import MaterialTable from '@material-table/core';

const ProjectInformation: React.FC = () => {
  const { setError } = useError();
  const navigate = useNavigate();
  const { name, updatedAt, createdAt, setName, setUpdatedAt, setCreatedAt } = useProjectStore();

  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { setBeforeCursor, setAfterCursor, setReQuery, pageAction, closeAction, resetStore } = usePaginationStore();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleOpenEdit = () => setOpenEdit(true);
  const handleCloseEdit = () => setOpenEdit(false);

  const [data, setData] = useState<any>([]);

  const [formValues, setFormValues] = useState({
    editName: '',
  });

  const columns = [
    { field: "name", title: "Name", },
  ];

  const queryProject = async () => {
    const res = await ProjectApi.getProject(getLocalStorage(PROJECT_ID));

    if (res.code) {
      setError(res.message);
    } else {
      setName(res.name);
      setUpdatedAt(res.updatedAt);
      setCreatedAt(res.createdAt);
    }

    const { limit, beforeCursor, afterCursor } = usePaginationStore.getState();
    const paginationParams = {
      limit: limit,
      beforeCursor: beforeCursor,
      afterCursor: afterCursor,
    };

    try {
      const resThings = await ProjectApi.getProjectThings(getLocalStorage(PROJECT_ID), paginationParams);
      if (resThings.code) {
        setError(resThings.message);
      } else {
        setData(resThings.data);

        if (resThings.metadata && resThings.metadata.total) {
          setTotalCount(resThings.metadata.total);
        }

        if (resThings.paging) {
          setAfterCursor(resThings.paging.afterCursor);
          setBeforeCursor(resThings.paging.beforeCursor);
        }
      }

    } catch (error) {
      console.error("Error fetching resThings data:", error);
    } finally {
      closeAction();
    }
  };

  const handleDelete = async () => {
    const res = await ProjectApi.deleteProject(getLocalStorage(PROJECT_ID));
    if (res.code) {
      setError(res.message);
    } else {
      handleClose();
      navigate(ROUTE_PROJECT);
    }
  };

  const handleUpdate = async () => {
    await ProjectApi.updateProject(getLocalStorage(PROJECT_ID), formValues.editName);
    await queryProject();
    handleCloseEdit();
  };

  const handleChange = (field: string, value: string) => {
    setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
  };

  const openEditDialog = () => {
    handleChange('editName', name);
    handleOpenEdit();
  }

  useEffect(() => {
    if (pageAction) {
      queryProject();
    }
  }, [pageAction]);

  useEffect(() => {
    resetStore();

    if (getLocalStorage(PROJECT_ID)) {
      queryProject();
    }
  }, [getLocalStorage(PROJECT_ID)]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <IconButtonBox
          icon={<KeyboardBackspaceIcon />}
          text='Back to project list'
          onClick={() => navigate(ROUTE_PROJECT)} />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box mb={4}>
          <Box display="flex" gap={2} alignItems="center">
            <Typography variant="h4" gutterBottom className='primaryBlueColor'>{name}</Typography>
          </Box>
        </Box>
        <Box>
          <OutlinedButton
            onClick={handleOpen}
            icon={<DeleteOutlineOutlinedIcon />}
            text='Delete' />
          <OutlinedButton
            onClick={openEditDialog}
            icon={<EditOutlinedIcon />}
            text='Edit' />
        </Box>
      </Box>
      <Paper sx={{ padding: 2 }}>
        <Typography variant="h6" gutterBottom>
          Information
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell>{name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Updated at</strong></TableCell>
                <TableCell>{updatedAt}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Created at</strong></TableCell>
                <TableCell>{createdAt}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Paper sx={{ padding: 2 }}>
        <Typography variant="h6" gutterBottom>
          Connected Things
        </Typography>
        <NewTableBox columns={columns} data={data} totalCount={totalCount} />
      </Paper>
      <DialogBox
        open={open}
        title='Delete'
        dialogContent={
          <>
            <span>{name} will be permanently deleted</span>
          </>
        }
        onCancel={handleClose}
        onConfirm={handleDelete}
        confirmLabel="Delete"
        isError={true}
      />
      <DialogBox
        open={openEdit}
        title="Edit name"
        dialogContent={
          <>
            <TextInputBox
              label="Name"
              placeholder='Name'
              required={true}
              value={formValues.editName}
              onChange={(e) => handleChange('editName', e.target.value)}
            />
          </>
        }
        onCancel={handleCloseEdit}
        onConfirm={handleUpdate}
        confirmLabel="Save"
      />
    </Box>
  );
};

export default ProjectInformation;
